import keycloakApi from './keycloak-auth';

/**
 * Available time periods for dashboard metrics.
 */
export type MetricsPeriod =
  | 'this_month'
  | 'last_month'
  | 'last_3_months'
  | 'last_6_months'
  | 'this_year'
  | 'all_time';

export const PERIOD_OPTIONS: { value: MetricsPeriod; label: string; shortTitle: string }[] = [
  { value: 'this_month', label: 'This month', shortTitle: 'MTD' },
  { value: 'last_month', label: 'Last month', shortTitle: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 months', shortTitle: '3 Month' },
  { value: 'last_6_months', label: 'Last 6 months', shortTitle: '6 Month' },
  { value: 'this_year', label: 'Year to date', shortTitle: 'YTD' },
  { value: 'all_time', label: 'All time', shortTitle: 'Total' },
];

/**
 * Dashboard metrics interface with all server-calculated statistics.
 */
export interface DashboardMetrics {
  paymentsByClient: number;
  totalPaymentVolume: number;
  totalPendingPaymentVolume: number;
  totalRecipients: number;
  totalSuppliers: number;
  totalClients: number;
  periodPaymentVolume: number;
  periodPaymentFiles: number;
  periodPayments: number;
  inProgramSuppliers: number;
  outOfProgramSuppliers: number;
  optimizedSuppliers: number;
  digitalAdoptionRate: number;
  totalProgramSpend: number;
  inProgramSpend: number;
  revenueGenerated: number;
  clientRebateShare: number;
  effectiveRebate: number;
  interchangeCreated: number;
  clientRebatePercentage: string | null;
  beneficiaryApproved: number;
  beneficiaryDenied: number;
  beneficiaryAwaitingApproval: number;
  beneficiaryAchCount: number;
  beneficiaryCheckCount: number;
  beneficiaryDigitalWalletCount: number;
}

const apiCall = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  return keycloakApi.authenticatedApiCall<T>(endpoint, options);
};

/**
 * Fetches all dashboard metrics from the server with calculations performed server-side.
 * @param period - Time period for aggregate stats (volume, files, payments)
 */
export const getDashboardMetrics = async (
  period: MetricsPeriod = 'this_month'
): Promise<DashboardMetrics> => {
  const response = await apiCall<{ partyId: string; metrics: DashboardMetrics }>(
    `/metrics?period=${period}`
  );
  return response.metrics;
};

/**
 * Formats cents to currency with K/M suffixes for large numbers.
 */
export const formatCurrency = (cents: number): string => {
  if (!Number.isFinite(cents) || Number.isNaN(cents)) {
    return '$0.00';
  }
  const dollars = cents / 100;
  const absDollars = Math.abs(dollars);

  if (absDollars >= 1_000_000) {
    return `$${(dollars / 1_000_000).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}M`;
  }
  if (absDollars >= 10_000) {
    return `$${(dollars / 1_000).toLocaleString(undefined, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}K`;
  }

  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
